import React, { useState, useEffect } from "react"
import { SimpleForm, 
    ReferenceInput,
    ReferenceArrayInput,
    AutocompleteArrayInput,
    SaveButton, 
    DeleteButton, 
    required, 
    useRecordContext, useDataProvider } from "react-admin"
import { ScreenEmpty } from "components/ui/screen"
import { useTranslate } from "ra-core"
import { Grid } from "@mui/material"
import { useController } from "react-hook-form"
import { useTimezoneSelect, allTimezones } from "react-timezone-select"
import * as forms from "components/ui/forms"
import { CompanyEntity } from "api/company/company.pb"
import { TextInputWithAudit, AutocompleteInputWithAudit, SelectInputWithAudit } from "components/audit/audit.inputs"
import CoordinateInputWithAudit from "components/audit/audit.coord.input"

interface props {
    readonly: boolean
    newRecord: boolean
}
const TableName = "objects"
const labelStyle = "original"
const timezones = {
  ...allTimezones,
}

function Form(props: React.PropsWithChildren<props>): JSX.Element  {
    const { readonly, newRecord } = props
    const translate = useTranslate()
    const { options } = useTimezoneSelect({ labelStyle, timezones })
    const dataProvider = useDataProvider()
    const [companies, setCompanies] = useState<CompanyEntity[]>([])

    useEffect(() => {
        dataProvider.getList("companies", {})
            .then(({ data }) => setCompanies(data))
            .catch(() => setCompanies([]))
    }, [dataProvider])


    const getCategoryFromID = (id: string): string =>  {
        const element = document.getElementById(`cat_${id}`)
        return element?.dataset?.varname || ""
    }

    const CategoryField = (): JSX.Element => {
        const record = useRecordContext()
        return <span data-varname={record?.varname} id={`cat_${record?.id}`}>{record?.name}</span>
    }

    const IndustrySelect = (): JSX.Element => {
        //const selectedCategory = useWatch({ name: 'object_category_id' })
        const record = useRecordContext()
        const { field: { value: selectedCategory } } = useController({ name: 'object_category_id' })
        const { field: { value: selectedIndustries } } = useController({ name: 'object_industry_ids' })
        const catVarname = getCategoryFromID(selectedCategory)

        if (catVarname !== "industry" && (selectedIndustries?.length === 0 || !record)) {
            return <></>
        }

        return (
             <forms.HierarchicalSelects
                readonly={readonly}
                source="object_industry_ids"
                label={translate("resources.objects.fields.object_industry_ids")}
                reference="object-industries" 
              />
        )
    }

    return (
            <SimpleForm toolbar={false}>
                <Grid container columnSpacing="8">
                    <Grid item xs={4}>
                        <TextInputWithAudit tableName={TableName} showAuditIcon={readonly} source="name" disabled={readonly} validate={[required()]} label={translate("resources.objects.fields.name")}/>
                    </Grid>
                    <Grid item xs={4}>
                        <TextInputWithAudit tableName={TableName} showAuditIcon={readonly} source="address" disabled={readonly} label={translate("resources.objects.fields.address")}/>
                    </Grid>
                </Grid>
                <Grid container columnSpacing="8">
                    <Grid item xs={4}>
                        <SelectInputWithAudit tableName={TableName} showAuditIcon={readonly} optionText="name" disabled={readonly} validate={[required()]} label={translate("internal.company_resource")} source="companies[resource]" choices={companies} />
                    </Grid>
                    <Grid item xs={4}>
                        <SelectInputWithAudit tableName={TableName} showAuditIcon={readonly} optionText="name"  disabled={readonly} validate={[required()]} label={translate("internal.company_customer")} source="companies[customer]" choices={companies} />
                    </Grid>
                </Grid>
                <Grid container columnSpacing="8">
                    <Grid item xs={4}>
                        <SelectInputWithAudit tableName={TableName} showAuditIcon={readonly} optionText="name"  disabled={readonly} label={translate("internal.company_service")} source="companies[service]" choices={companies} />
                    </Grid>
                </Grid>
                <Grid container columnSpacing="8">
                    <Grid item xs={4}>
                        <ReferenceInput label={translate("resources.objects.fields.object_category_id")} source="object_category_id" reference="object-categories">
                            <SelectInputWithAudit tableName={TableName} showAuditIcon={readonly} disabled={readonly} optionText={<CategoryField/>} />
                        </ReferenceInput>
                    </Grid>
                    <Grid item xs={4}>
                        <IndustrySelect />
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item xs={6}>
                        <ReferenceArrayInput
                            label={translate("resources.objects.fields.parent_ids")}
                            source="parent_ids"
                            reference="objects"
                            filterToQuery={(searchText: string) => ({ name: searchText })}
                            //filter={{ isActive: true }}
                        >
                            <AutocompleteArrayInput optionText="name" disabled={readonly} />
                        </ReferenceArrayInput>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item xs={6}>
                        <AutocompleteInputWithAudit
                            tableName={TableName} 
                            showAuditIcon={readonly}
                            source="timezone"
                            disabled={readonly}
                            label={translate("resources.common.fields.timezone")}
                            validate={[required()]}
                            choices={options}
                            /*choices={options.map(option => ({
                                id: option.value,
                                name: translate(option.label)
                            })}*/
                            optionText="label"
                            optionValue="value"
                            fullWidth />
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item xs={6}>
                         <CoordinateInputWithAudit tableName={TableName} showAuditIcon={readonly} source="geoposition" disabled={readonly} label={translate("resources.objects.fields.geoposition")}/>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item xs={8}>
                         <TextInputWithAudit tableName={TableName} showAuditIcon={readonly} source="description" disabled={readonly} multiline rows={5} label={translate("resources.common.fields.description")}/>
                    </Grid>
                </Grid>
                <ScreenEmpty enabled={!readonly}>
                    <Grid container>
                        <Grid item xs={6}>
                            <SaveButton />
                        </Grid>
                        <ScreenEmpty enabled={!newRecord}>
                            <Grid item xs={6}>
                                <DeleteButton />
                            </Grid>
                        </ScreenEmpty>
                    </Grid>
                </ScreenEmpty>
            </SimpleForm>
    )
}

Form.defaultProps = {
	readonly: false,
    newRecord: false,
}

export default Form